import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { SectionHeader } from '@/shared/ui/SectionHeader'
import { Selectbutton } from '@/shared/ui/Selectbutton'
import { Dropdown } from '@/shared/ui/Dropdown'
import { useCommonCodes } from '@/shared/lib/codes'
import { figmaLabel, sortByFigma } from '@/shared/lib/figmaLabel'
import type { OrgNewDraft } from '@/public/features/org/types'
import { HELP_TYPES, ORG_LAST_STEP } from '@/public/features/org/constants/orgNew'
import {
  OrgNewPcShell,
  PC_ASSISTIVE_BUTTON_CLASS,
  PC_PRIMARY_BUTTON_CLASS,
} from '@/public/features/org/ui/orgNewPc/parts'

/**
 * 칩 묶음의 폭 — 구역마다 시안 값이 다르다(1288:22088).
 * 칩은 늘어나되 `min`~`max` 사이에서만 늘어난다.
 */
const CHIP_WIDTH = {
  category: '[&>.ui-selectbutton]:min-w-[166px] [&>.ui-selectbutton]:max-w-[190px]',
  target: '[&>.ui-selectbutton]:min-w-[150px] [&>.ui-selectbutton]:max-w-[158px]',
  // 03·04 는 시안이 칩 폭을 190 으로 못박았다 — `flex` 로 두면 마지막 줄만 200 이 되어
  // 같은 구역 안에서 폭이 갈린다(실측: 190.4 와 200 이 섞였다).
  help: '[&>.ui-selectbutton]:w-[190px]',
  resource: '[&>.ui-selectbutton]:w-[190px]',
} as const

/**
 * 시안의 Category Chip Group — flex-wrap · gap 12.
 * `flex` 를 주면 **줄마다 칩 수가 달라 폭이 갈린다.** 폭을 못박은 구역은 그것을 쓰지 않는다.
 */
function ChipGroup({ width, children }: { width: string; children: ReactNode }) {
  const grow = width.includes(':w-[') ? '' : '[&>.ui-selectbutton]:flex-1'

  return (
    <div className={`flex w-full flex-wrap content-start items-start gap-3 ${grow} ${width}`}>
      {children}
    </div>
  )
}

/**
 * PC 네 번째 페이지 — 모바일 6~10 단계(분야 · 대상 · 도움종류 · 서비스 · 제공범위)를
 * 한 화면에 묶는다(시안 1288:22088).
 */
export function OrgNewPcCard({ draft }: { draft: OrgNewDraft }) {
  const navigate = useNavigate()

  const categories = useCommonCodes('category')
  // 대상은 코드표가 두 그룹으로 갈려 있다 — 모바일 `StepCardTarget` 과 같은 순서로 이어 붙인다.
  const lifecycle = useCommonCodes('target_lifecycle')
  const situation = useCommonCodes('target_situation')
  const targets = [...(lifecycle.data ?? []), ...(situation.data ?? [])]
  const resources = useCommonCodes('resource')

  // 판정은 draft 가 갖고 있다 — `firstIncompleteStep` 은 전부 끝나도 `TOTAL_STEPS` 를
  // 돌려주므로 그것으로는 «다 됐다»를 가릴 수 없다(실측).
  // **`goNext` 는 주소만 다음 단계로 옮긴다** — 해결카드를 만드는 API 를 부르는 것은 `submit` 이다.
  const canSubmit = draft.isCardComplete

  return (
    <OrgNewPcShell
      title="해결카드 등록하기"
      active="card-select"
      error={draft.error}
      footer={
        <>
          <button
            type="button"
            className={`${PC_ASSISTIVE_BUTTON_CLASS} w-[143px]`}
            onClick={() => navigate(`/orgs/new/${ORG_LAST_STEP}`)}
          >
            이전
          </button>
          <button
            type="button"
            className={`${PC_PRIMARY_BUTTON_CLASS} w-[250px]`}
            disabled={!canSubmit || draft.isSubmitting}
            onClick={draft.submit}
          >
            {draft.isSubmitting ? '등록하는 중…' : '해결 카드 만들기'}
          </button>
        </>
      }
    >
      <div className="flex w-full flex-col items-start gap-[46px]">
        <section className="flex w-full flex-col items-start gap-6">
          <SectionHeader
            no="01"
            title="어떤 분야의 문제를 해결할 수 있나요?"
            desc="해결할 수 있는 분야를 선택해 주세요."
          />
          <ChipGroup width={CHIP_WIDTH.category}>
            {sortByFigma(categories.data ?? []).map((c) => (
              <Selectbutton
                key={c.code}
                size="lg"
                label={figmaLabel(c.code, c.label)}
                isOn={draft.cardCategoryCodes.includes(c.code)}
                onSelect={() => draft.toggleCardCategory(c.code)}
              />
            ))}
          </ChipGroup>
        </section>

        <section className="flex w-full flex-col items-start gap-6">
          <SectionHeader
            no="02"
            title="해결 대상이 누구인가요?"
            desc="대상의 특성을 선택해 주세요."
          />
          <ChipGroup width={CHIP_WIDTH.target}>
            {targets.map((t) => (
              <Selectbutton
                key={t.code}
                size="lg"
                label={figmaLabel(t.code, t.label)}
                isOn={draft.cardTargetCodes.includes(t.code)}
                onSelect={() => draft.toggleCardTarget(t.code)}
              />
            ))}
          </ChipGroup>
        </section>

        <section className="flex w-full flex-col items-start gap-6">
          <SectionHeader
            no="03"
            title="도움의 종류는 무엇인가요?"
            desc="제공 가능한 도움의 종류를 선택해 주세요."
          />
          <ChipGroup width={CHIP_WIDTH.help}>
            {HELP_TYPES.map((h) => (
              <Selectbutton
                key={h.code}
                size="lg"
                label={h.label}
                isOn={draft.cardHelpCodes.includes(h.code)}
                onSelect={() => draft.toggleCardHelp(h.code)}
              />
            ))}
          </ChipGroup>
        </section>

        <section className="flex w-full flex-col items-start gap-6">
          <SectionHeader
            no="04"
            title="제공 가능한 서비스는 무엇인가요?"
            desc="제공 가능한 서비스의 종류를 선택해 주세요."
          />
          <ChipGroup width={CHIP_WIDTH.resource}>
            {(resources.data ?? []).map((r) => (
              <Selectbutton
                key={r.code}
                size="lg"
                label={r.label}
                isOn={draft.cardResourceCodes.includes(r.code)}
                onSelect={() => draft.toggleCardResource(r.code)}
              />
            ))}
          </ChipGroup>
        </section>

        <ScopeSection draft={draft} />
      </div>
    </OrgNewPcShell>
  )
}

/**
 * 05 구역 — 모바일은 세 축을 칩으로 그리는데 **PC 시안은 드롭다운 셋**이다(1288:22901).
 * 세 칸의 폭은 시안대로 같게 나눠 갖는다(각 317.333 = (1000 - 24×2) / 3).
 */
function ScopeSection({ draft }: { draft: OrgNewDraft }) {
  const areas = useCommonCodes('service_area')
  const costs = useCommonCodes('cost_type')
  const pilots = useCommonCodes('pilot_available')

  const toOptions = (items: { code: string; label: string }[]) =>
    items.map((i) => ({ value: i.code, label: i.label }))

  return (
    <section className="flex w-full flex-col items-start gap-6">
      <SectionHeader
        no="05"
        title="제공 범위와 조건을 알려주세요."
        desc="지역, 비용, 실증 참여 여부를 선택해 주세요."
      />
      <div className="flex w-full items-start gap-6">
        <ScopeField label="제공 지역">
          <Dropdown
            size="pc"
            ariaLabel="제공 지역"
            placeholder="선택해 주세요."
            options={toOptions(areas.data ?? [])}
            value={draft.cardAreaCodes[0] ?? null}
            onChange={draft.selectCardArea}
          />
        </ScopeField>
        <ScopeField label="이용 비용">
          <Dropdown
            size="pc"
            ariaLabel="이용 비용"
            placeholder="선택해 주세요."
            options={toOptions(costs.data ?? [])}
            value={draft.cardCostType === '' ? null : draft.cardCostType}
            onChange={draft.selectCardCost}
          />
        </ScopeField>
        <ScopeField label="실증(시범사업) 참여">
          <Dropdown
            size="pc"
            ariaLabel="실증(시범사업) 참여"
            placeholder="선택해 주세요."
            options={toOptions(pilots.data ?? [])}
            value={draft.cardPilot === '' ? null : draft.cardPilot}
            onChange={draft.selectCardPilot}
          />
        </ScopeField>
      </div>
    </section>
  )
}

/** 라벨 줄 높이가 1.7 인 것까지 시안 값이다(1288:22847). */
function ScopeField({ label, children }: { label: string; children: ReactNode }) {
  return (
    // `items-stretch` 다 — `items-start` 로 두면 드롭다운이 글자 폭으로 줄어든다(실측).
    <div className="flex min-w-px flex-1 shrink-0 grow basis-0 flex-col gap-[6px]">
      <span className="self-start text-body-md-reading font-normal text-black-600">{label}</span>
      {children}
    </div>
  )
}
